/**
 * Tracking Event Catalog
 *
 * Single source of truth for every custom event we emit to PostHog
 * (and, for select events, to GA/Intercom).
 *
 * Naming rules:
 *   - snake_case.
 *   - Prefix by feature area (e.g. `discovery_`, `strategy_`).
 *   - Past-tense or noun phrase.
 *
 * Every call to `trackingService.track(event, props)` must use one of
 * these constants. The union `TrackingEventName` is the gate.
 */

export const TrackingEvents = {
  CLAUDE_CONNECTOR_CLICKED: "claude_connector_clicked",
  SETUPV2_PILOT_EVENT: "setupv2_pilot_event",
  SUPPORT_SESSION_OUTCOME: "support_session_outcome",
  // Marketing / public site
  MARKETING_PAGE_VIEW: "marketing_page_view",
  MARKETING_SECTION_VIEW: "marketing_section_view",
  MARKETING_CTA_CLICK: "marketing_cta_click",
  MARKETING_CTA_CLICKED: "marketing_cta_clicked",
  MARKETING_OUTBOUND_CLICK: "marketing_outbound_click",
  MARKETING_PROVIDER_CARD_CLICK: "marketing_provider_card_click",
  MARKETING_VIDEO_PLAY: "marketing_video_play",
  MARKETING_SCROLL_DEPTH: "marketing_scroll_depth",
  MARKETING_HOMEPAGE_VIEWED: "marketing_homepage_viewed",
  HOMEPAGE_V2_INTERACTION: "homepage_v2_interaction",
  MARKETING_HOMEPAGE_SECTION_VIEWED: "marketing_homepage_section_viewed",
  MARKETING_HOMEPAGE_STORY_PANEL_VIEWED: "marketing_homepage_story_panel_viewed",
  MARKETING_HOMEPAGE_PRODUCT_VIEWED: "marketing_homepage_product_viewed",
  MARKETING_HOMEPAGE_PRODUCT_SELECTED: "marketing_homepage_product_selected",
  MARKETING_HOMEPAGE_PROVIDER_VIEWED: "marketing_homepage_provider_viewed",
  MARKETING_HOMEPAGE_CAPABILITY_SELECTED: "marketing_homepage_capability_selected",
  MARKETING_HOMEPAGE_NAVIGATION_CLICKED: "marketing_homepage_navigation_clicked",
  MARKETING_HOMEPAGE_MENU_TOGGLED: "marketing_homepage_menu_toggled",
  MARKETING_HOMEPAGE_DOCUMENT_OPENED: "marketing_homepage_document_opened",
  MARKETING_HOMEPAGE_CTA_CLICKED: "marketing_homepage_cta_clicked",

  // Auth
  AUTH_SIGNUP_PAGE_VIEW: "auth_signup_page_view",
  AUTH_SIGNUP_START: "auth_signup_start",
  AUTH_SIGNUP_EMAIL_SUBMITTED: "auth_signup_email_submitted",
  AUTH_SIGNUP_OTP_SENT: "auth_signup_otp_sent",
  AUTH_SIGNUP_OTP_SUBMITTED: "auth_signup_otp_submitted",
  AUTH_SIGNUP_COMPLETE: "auth_signup_complete",
  AUTH_SIGNUP_ERROR: "auth_signup_error",
  AUTH_LOGIN_PAGE_VIEW: "auth_login_page_view",
  AUTH_LOGIN_START: "auth_login_start",
  AUTH_LOGIN_EMAIL_SUBMITTED: "auth_login_email_submitted",
  AUTH_LOGIN_OTP_SENT: "auth_login_otp_sent",
  AUTH_LOGIN_OTP_SUBMITTED: "auth_login_otp_submitted",
  AUTH_LOGIN_SUCCESS: "auth_login_success",
  AUTH_LOGIN_ERROR: "auth_login_error",
  AUTH_GOOGLE_SSO_CLICK: "auth_google_sso_click",
  AUTH_GOOGLE_SSO_SUCCESS: "auth_google_sso_success",
  AUTH_GOOGLE_SSO_ERROR: "auth_google_sso_error",
  AUTH_ACCOUNT_CREATED: "auth_account_created",
  AUTH_LOGOUT: "auth_logout",
  // Fires once per AuthPageV2 mount to record that the provider-logo
  // social-proof strip was rendered alongside the auth form. Lets us
  // attribute login/signup conversion lift to the strip independently
  // of the other V2 redesign changes (see docs/specs/2026-04-20-*).
  AUTH_SOCIAL_PROOF_VIEW: "auth_social_proof_view",
  AUTH_PARTNER_OFFER_VIEW: "auth_partner_offer_view",
  // Records support-link intent; it does not confirm offer redemption.
  AUTH_PARTNER_SUPPORT_CLICK: "auth_partner_support_click",

  // Onboarding
  ONBOARDING_START: "onboarding_start",
  ONBOARDING_QUESTION_VIEW: "onboarding_question_view",
  ONBOARDING_QUESTION_ANSWERED: "onboarding_question_answered",
  ONBOARDING_STEP_SKIP: "onboarding_step_skip",
  ONBOARDING_STEP_BACK: "onboarding_step_back",
  ONBOARDING_VALIDATION_ERROR: "onboarding_validation_error",
  ONBOARDING_SAVE_PROGRESS: "onboarding_save_progress",
  ONBOARDING_COMPLETE: "onboarding_complete",
  ONBOARDING_ABANDON: "onboarding_abandon",
  ONBOARDING_RESUME: "onboarding_resume",

  // Questionnaire (shared InvestorQuestionnaire component; PostHog mirror of
  // the Intercom questionnaire events. CONV-lane C2 gate -- see
  // docs/specs/conv-funnel-instrumentation-and-weekly-agent-2026-06-12.md)
  QUESTIONNAIRE_STARTED: "questionnaire_started",
  QUESTIONNAIRE_COMPLETED: "questionnaire_completed",

  // Navigation & app shell
  NAV_ROUTE_CHANGE: "nav_route_change",
  NAV_SIDEBAR_TOGGLE: "nav_sidebar_toggle",
  NAV_MENU_CLICK: "nav_menu_click",
  NAV_ROLE_SWITCH: "nav_role_switch",
  NAV_THEME_TOGGLE: "nav_theme_toggle",
  NAV_SEARCH_OPEN: "nav_search_open",
  NAV_HELP_OPEN: "nav_help_open",
  NAV_NOTIFICATIONS_OPEN: "nav_notifications_open",

  // Profile & settings
  PROFILE_VIEW: "profile_view",
  PROFILE_EDIT_START: "profile_edit_start",
  PROFILE_FIELD_UPDATE: "profile_field_update",
  PROFILE_SAVE: "profile_save",
  PROFILE_PICTURE_UPLOAD: "profile_picture_upload",
  PROFILE_PASSWORD_RESET_REQUEST: "profile_password_reset_request",

  // Discovery
  DISCOVERY_VIEW: "discovery_view",
  DISCOVERY_FILTER_APPLY: "discovery_filter_apply",
  DISCOVERY_FILTER_CLEAR: "discovery_filter_clear",
  DISCOVERY_SORT_CHANGE: "discovery_sort_change",
  DISCOVERY_SEARCH: "discovery_search",
  DISCOVERY_VIEW_MODE_CHANGE: "discovery_view_mode_change",
  DISCOVERY_CARD_CLICK: "discovery_card_click",
  // Like/unlike a product from a Discover surface. Fires on the optimistic
  // toggle (click intent), with `liked` = the post-click state and `surface`
  // distinguishing listing vs detail. PWA-2205.
  DISCOVERY_PRODUCT_LIKE: "discovery_product_like",
  DISCOVERY_PAGINATION: "discovery_pagination",
  DISCOVERY_COMPARE_START: "discovery_compare_start",
  DISCOVERY_EMPTY_STATE: "discovery_empty_state",
  // Bulk CSV export of the filtered screener result set (PWA-2490). Fires once
  // on completion with the row counts actually written to the two files.
  DISCOVERY_BULK_EXPORT: "discovery_bulk_export",

  // Product detail
  PRODUCT_DETAIL_VIEW: "product_detail_view",
  PRODUCT_TAB_CHANGE: "product_tab_change",
  PRODUCT_BENCHMARK_TOGGLE: "product_benchmark_toggle",
  PRODUCT_HIDDEN_POSITIONS_CTA_CLICK: "product_hidden_positions_cta_click",
  /** @deprecated Pre-V4 detail page tab. V4 detail page uses stocks/baskets/options composition tabs -- use PRODUCT_TAB_CHANGE. */
  PRODUCT_HOLDINGS_VIEW: "product_holdings_view",
  /** @deprecated Pre-V4 detail page tab. V4 detail page has no standalone performance tab. */
  PRODUCT_PERFORMANCE_VIEW: "product_performance_view",
  /** @deprecated Pre-V4 detail page tab. V4 detail page has no standalone risk tab. */
  PRODUCT_RISK_VIEW: "product_risk_view",
  /** @deprecated Pre-V4 detail page tab. V4 detail page has no standalone commentary tab. */
  PRODUCT_COMMENTARY_VIEW: "product_commentary_view",
  PRODUCT_SHARE_CLICK: "product_share_click",
  PRODUCT_BACK_CLICK: "product_back_click",
  PRODUCT_COMMENTS_VIEW: "product_comments_view",
  PRODUCT_COMMENT_SUBMIT: "product_comment_submit",

  // Strategy
  STRATEGY_CREATE_START: "strategy_create_start",
  STRATEGY_TEMPLATE_SELECT: "strategy_template_select",
  STRATEGY_ADD_TO_PORTFOLIO: "strategy_add_to_portfolio",
  STRATEGY_REMOVE_FROM_PORTFOLIO: "strategy_remove_from_portfolio",
  STRATEGY_WEIGHT_CHANGE: "strategy_weight_change",
  STRATEGY_SIDE_CHANGE: "strategy_side_change",
  STRATEGY_SAVE: "strategy_save",
  STRATEGY_DELETE: "strategy_delete",
  STRATEGY_LAUNCH_CONFIRM: "strategy_launch_confirm",
  STRATEGY_LAUNCH_SUBMIT: "strategy_launch_submit",
  STRATEGY_LAUNCH_ERROR: "strategy_launch_error",
  // Allocation methodology acknowledgment gate (first-save education)
  STRATEGY_ALLOCATION_ACK_SHOWN: "strategy_allocation_ack_shown",
  STRATEGY_ALLOCATION_ACK_CONFIRM: "strategy_allocation_ack_confirm",
  STRATEGY_ALLOCATION_ACK_ERROR: "strategy_allocation_ack_error",
  STRATEGY_ALLOCATION_ACK_PROCEED_UNSAVED: "strategy_allocation_ack_proceed_unsaved",

  // Activity / orders / trades (investor)
  ACTIVITY_VIEW: "activity_view",
  ACTIVITY_TAB_CHANGE: "activity_tab_change",
  ACTIVITY_FILTER_CHANGE: "activity_filter_change",
  ACTIVITY_REFRESH: "activity_refresh",
  ACTIVITY_PAGINATION: "activity_pagination",
  ACTIVITY_EXPORT_DOWNLOAD: "activity_export_download",
  ACTIVITY_ENROLLMENT_GATE_VIEW: "activity_enrollment_gate_view",
  ACTIVITY_ENROLLMENT_CTA_CLICK: "activity_enrollment_cta_click",
  /** @deprecated Rebalance flow lives on provider/admin pages. Investor activity page is a read-only trades/orders explorer. */
  ACTIVITY_REBALANCE_START: "activity_rebalance_start",
  /** @deprecated See ACTIVITY_REBALANCE_START. */
  ACTIVITY_REBALANCE_PREVIEW: "activity_rebalance_preview",
  /** @deprecated See ACTIVITY_REBALANCE_START. */
  ACTIVITY_REBALANCE_SUBMIT: "activity_rebalance_submit",
  /** @deprecated See ACTIVITY_REBALANCE_START. */
  ACTIVITY_REBALANCE_ERROR: "activity_rebalance_error",
  /** @deprecated No withdraw flow on investor activity page. */
  ACTIVITY_WITHDRAW_START: "activity_withdraw_start",
  /** @deprecated No close-position flow on investor activity page. */
  ACTIVITY_POSITION_CLOSE: "activity_position_close",
  /** @deprecated No per-order detail view on investor activity page. */
  ACTIVITY_ORDER_DETAIL_VIEW: "activity_order_detail_view",

  // Provider portal
  PROVIDER_DASHBOARD_VIEW: "provider_dashboard_view",
  PROVIDER_BASKET_VIEW: "provider_basket_view",
  PROVIDER_BASKET_EDIT_START: "provider_basket_edit_start",
  PROVIDER_BASKET_SAVE: "provider_basket_save",
  PROVIDER_BASKET_PUBLISH: "provider_basket_publish",
  PROVIDER_PORTFOLIO_EDIT_START: "provider_portfolio_edit_start",
  PROVIDER_PORTFOLIO_SAVE: "provider_portfolio_save",
  PROVIDER_REBALANCE_QUEUE: "provider_rebalance_queue",
  PROVIDER_COMMENTARY_PUBLISH: "provider_commentary_publish",
  PROVIDER_INSIGHTS_VIEW: "provider_insights_view",

  // Admin
  ADMIN_DASHBOARD_VIEW: "admin_dashboard_view",
  ADMIN_USER_LIST_VIEW: "admin_user_list_view",
  ADMIN_USER_IMPERSONATE: "admin_user_impersonate",
  ADMIN_USER_ROLE_CHANGE: "admin_user_role_change",
  ADMIN_PRODUCT_MODERATION: "admin_product_moderation",
  ADMIN_REBALANCE_APPROVE: "admin_rebalance_approve",
  ADMIN_REBALANCE_REJECT: "admin_rebalance_reject",
  ADMIN_BULK_ACTION: "admin_bulk_action",

  // Sharing
  SHARE_LINK_COPY: "share_link_copy",
  SHARE_LINK_VISIT: "share_link_visit",
  SHARE_LINK_RESOURCE_VIEW: "share_link_resource_view",
  SHARE_SOCIAL_CLICK: "share_social_click",
  PRODUCT_SHARE_VIEW: "product_share_view",
  PRODUCT_SHARE_SECTION_VIEW: "product_share_section_view",
  PRODUCT_SHARE_CTA_CLICK: "product_share_cta_click",
  PRODUCT_SHARE_PERIOD_CHANGE: "product_share_period_change",
  PRODUCT_SHARE_UNLOCK: "product_share_unlock",
  SHARE_GATE_BAR_TAP: "share_gate_bar_tap",
  SHARE_GATE_NUDGE: "share_gate_nudge",

  // Errors & performance
  ERROR_BOUNDARY_CAUGHT: "error_boundary_caught",
  ERROR_API: "error_api",
  ERROR_VALIDATION: "error_validation",

  // Engagement milestones
  ENGAGEMENT_RETURN_VISIT: "engagement_return_visit",
  ENGAGEMENT_SESSION_DURATION_30S: "engagement_session_duration_30s",
  ENGAGEMENT_SESSION_DURATION_2M: "engagement_session_duration_2m",
  ENGAGEMENT_SESSION_DURATION_10M: "engagement_session_duration_10m",

  // Alpha setup. New conversion spine. Do not reuse questionnaire_*,
  // onboarding_*, profile_ibkr_*, or strategy_launch_* for this path.
  ALPHA_SETUP_VIEWED: "alpha_setup_viewed",
  ALPHA_CERTIFICATION_VIEWED: "alpha_certification_viewed",
  ALPHA_CERTIFICATION_ACKNOWLEDGED: "alpha_certification_acknowledged",
  ALPHA_CERTIFICATION_VERIFIED: "alpha_certification_verified",
  ALPHA_CERTIFICATION_ERROR: "alpha_certification_error",
  ALPHA_PREFERENCE_SAVED: "alpha_preference_saved",
  ALPHA_BROKERAGE_CHOICE_SELECTED: "alpha_brokerage_choice_selected",
  ALPHA_IBKR_SETUP_SELF_REPORTED: "alpha_ibkr_setup_self_reported",
  ALPHA_RISK_PROFILE_COMPLETED: "alpha_risk_profile_completed",
  ALPHA_AGREEMENT_SIGNED: "alpha_agreement_signed",
  ALPHA_PLAN_REVIEW_COMPLETED: "alpha_plan_review_completed",
  ALPHA_BROKERAGE_PROVISIONED: "alpha_brokerage_provisioned",
  ALPHA_CUSTOMER_LAUNCHED: "alpha_customer_launched",
  ALPHA_LAUNCHED: "alpha_launched",
} as const;

export type TrackingEventName = (typeof TrackingEvents)[keyof typeof TrackingEvents];

/**
 * Loose prop bag for tracking events. We deliberately allow unknown keys so
 * feature code can attach contextual data without schema churn. The event
 * name itself is the strict part of the contract; property schemas evolve.
 */
export type TrackingEventProps = Record<string, string | number | boolean | null | undefined>;
